import { FC, useEffect, useRef, useState } from 'react'
import { CircularProgress, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material'
import TinyMCEEditor from '../../../components/tinymce'
import { ContainedButton, OutlinedButton } from '../styled'
import { editDocumentation } from '../../../services/document.service'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../store/store'
import { incrementDocumentationStep } from '../../../store/documentSlice'
import './style.scss'
import { Editor } from 'tinymce'
import { addJiraCommentForTask, updateConfluenceLinkField } from '../../../services/jira.service'
import { toast } from 'react-toastify';

interface DocInTinyMceProps {
  handleClosePopup: () => void
  requiredIssueDetails: any
  setArticleLink: React.Dispatch<React.SetStateAction<string>>
}

const EditDocument: FC<DocInTinyMceProps> = ({ handleClosePopup, requiredIssueDetails, setArticleLink }) => {
  const dispatch = useDispatch()
  const documentState = useSelector((state: RootState) => state.document)
  const [documentationContentInside, setDocumentationContentInside] = useState<any>({
    "name": "",
    "description": "",
    "core_functionalities": [],
    "additional_notes": "",
    "text": "",
    "article_link": ""
  })
  const [loading, setLoading] = useState(false)
  const editorRef = useRef<Editor | null>(null);

  const jsonToHTML = (data: any): string => {
    if (typeof data === 'string') {
      // Convert newlines to <br/>
      data = data.replace(/\n/g, '<br/>');

      // Handle bold text (surrounded by **)
      data = data.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

      // Handle code blocks (triple backticks)
      data = data.replace(/```(.*?)```/g, '<pre class="language-markup"><code>$1</code></pre>');

      // Handle headings (for example, **Retrieving Public UUID:** becomes <h2>)
      if (/^\*\*/.test(data)) {
        data = `<h2>${data.replace(/\*\*(.*?)\*\*/g, '$1')}</h2>`;
      }

      return `<p>${data}</p>`;
    }

    if (Array.isArray(data)) {
      return `<ul>${data.map(item => `<li>${jsonToHTML(item)}</li>`).join('')}</ul>`;
    }

    if (typeof data === 'object' && data !== null) {
      return Object.entries(data)
        .map(([key, value]) => {
          return `<div><strong>${key}:</strong> ${jsonToHTML(value)}</div>`;
        })
        .join('');
    }

    return '';
  };

  const handleAddArticleLinkToJira = async (articleLink: any) => {
    try {
      const commentPayload = {
        issueKey: requiredIssueDetails?.key,
        body: `Documentation: ${articleLink}`
      }
      const customFieldPayload = {
        issueKey: requiredIssueDetails?.key,
        articleLink: articleLink
      }
      await addJiraCommentForTask(commentPayload);
      await updateConfluenceLinkField(customFieldPayload);
      toast.success('Comment and confluence link Added successfully');
    } catch (error) {
      toast.error('Error adding article link to Jira issue');
    }
   }

  const handleSaveDocumentation = async () => {
    try {
      if (Object.keys(documentationContentInside || {}).length) {
        setLoading(true)
        const documentResponse = await editDocumentation({
          "article_id": documentState?.documentationContent?.article_id || "",
          "name": documentationContentInside?.name || "",
          "text": jsonToHTML(documentationContentInside) || "",
          "issue_id": requiredIssueDetails.id || ""
        })
        if (documentResponse?.article_id) {
          await handleAddArticleLinkToJira(documentResponse?.article_link);
          setArticleLink(documentResponse?.article_link)
          dispatch(incrementDocumentationStep())
        }
      }
    } catch (e: any) {
      console.error("Error in save document", e?.message || e)
    } finally {
      setLoading(false)
    }
  }

  const getDisableStatus = () => {
    const { name, description, core_functionalities, additional_notes } = documentationContentInside
    return !name || !description || !core_functionalities || !additional_notes
  }

  useEffect(() => {
    if (documentState.documentationContent) {
      setDocumentationContentInside({ ...documentState.documentationContent, core_functionalities: jsonToHTML(documentState?.documentationContent?.core_functionalities || {}) })
    }
  }, [documentState.documentationContent])

  return (
    <div className='DocInTinyMce-container'>
      <DialogTitle>
        <div className='document-header'>Documentation for your Code </div>
        <div className='document-description'>feel free to edit and save the document</div>
        {documentationContentInside?.article_link && <div className='article-link'><a href={documentationContentInside?.article_link} target='_blank'>Click here</a> to view the article</div>}
      </DialogTitle>
      <DialogContent className='doc-edit-body-container'>
        <div className='each-doc-edit-container'>
          <div className='label'>Name</div>
          <TextField
            variant="outlined"
            className='txt-field'
            type="text"
            value={documentationContentInside?.name || ""}
            onChange={(e) => { setDocumentationContentInside((prev: any) => ({ ...prev, name: e.target.value })) }}
          />
        </div>
        <div className='each-doc-edit-container'>
          <div className='label'>Description</div>
          <TextField
            minRows={3}
            maxRows={5}
            multiline
            className='txt-area'
            value={documentationContentInside?.description || ""}
            onChange={(e) => { setDocumentationContentInside((prev: any) => ({ ...prev, description: e.target.value })) }}
          />
        </div>
        <div className='each-doc-edit-container'>
          <div className='label'>Core functionalities</div>
          <TinyMCEEditor
            handleOnChange={(e: any) => { setDocumentationContentInside((prev: any) => ({ ...prev, core_functionalities: e })) }}
            editorValue={documentationContentInside?.core_functionalities}
            editorRef={editorRef}
          />
        </div>
        <div className='each-doc-edit-container'>
          <div className='label'>Additional notes:</div>
          <TextField
            multiline
            minRows={3}
            maxRows={5}
            className='txt-area'
            value={documentationContentInside?.additional_notes || ""}
            onChange={(e) => { setDocumentationContentInside((prev: any) => ({ ...prev, additional_notes: e.target.value })) }}
          />
        </div>

      </DialogContent>
      <DialogActions>
        <div className='dialog-action-container'>
          <OutlinedButton onClick={handleClosePopup}>
            Cancel
          </OutlinedButton>
          <ContainedButton disabled={getDisableStatus()} onClick={handleSaveDocumentation}>
            {loading ? <CircularProgress style={{ color: 'white', marginLeft: '5px' }} size='1rem' /> :
              'Save'}
          </ContainedButton>
        </div>
      </DialogActions>
    </div>
  )
}

export default EditDocument